import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import {
  Sparkles,
  RefreshCw,
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Search,
  Edit3,
  Plus,
  Check,
  X,
  TrendingUp,
  Mail,
  Globe,
  Bot,
  Sliders,
  Layers,
  Activity,
  Send,
  Cpu,
  Lock,
  Play,
  Pause,
  Award,
  FileText,
} from 'lucide-react';
import {
  fetchDailyOverview,
  triggerAgentReflection,
  executeManagerAction,
  updateAiBudgetSettings,
  sendTestSlackAlert,
  syncCompetitorInboxWatch,
  syncSendoutQaWatch,
  updateSoloExperiment,
  DailyOverviewData,
  AgentDomain,
  SoloExperiment,
  SoloExperimentVariant,
} from '../services/dailyOverviewApi';

const GEO_FLAGS: Record<string, string> = {
  SE: '🇸🇪 SE',
  NO: '🇳🇴 NO',
  FI: '🇫🇮 FI',
  DK: '🇩🇰 DK',
  DE: '🇩🇪 DE',
  ALL: '🌍 ALL',
};

const FALLBACK_EXPERIMENT: SoloExperiment = {
  id: 'exp-meta-no-20261026',
  title: '2-Week Facebook (Meta) Ads Sprint — Landing Page & Creative Split Test',
  channel: 'Meta Ads (NO)',
  geo: 'NO',
  status: 'collecting_data',
  startsAt: '2026-10-26',
  endsAt: '2026-11-09',
  targetClicks: 500,
  currentClicks: 0,
  progressPct: 0,
  currentSpendNok: 0,
  stopLossRule: 'Auto-pause any variant spending > 600 NOK with < 2.0% lender click-out rate (/slgo/).',
  successCriteria: 'Reach >= 4.5% lender click-out rate (/slgo/) at <= 2.50 NOK CPC (~55 NOK per lender click-out).',
  snoozedChannels: ['Google Ads RSAs (Snoozed)', 'Microsoft Ads RSAs (Snoozed)'],
  variants: [
    {
      id: 'var-lp-calc',
      label: 'Variant A · Pre-filled Loan Calculator',
      track: 'Landing Page + Flow',
      destinationPath: '/lanekalkulator/?amount=150000&years=5',
      creativeHook: 'Se hvor mye du kan kutte månedskostnaden på 30 sekunder (150 000 kr / 5 år)',
      status: 'active',
      clicks: 0,
      spendNok: 0,
      cpcNok: 0,
      lenderClickouts: 0,
      clickoutRatePct: 0,
    },
    {
      id: 'var-lp-samlelan',
      label: 'Variant B · Samlelån Comparison Table (Control)',
      track: 'Landing Page (Baseline)',
      destinationPath: '/lan-penger/samlelan/',
      creativeHook: 'Samle dyre smålån og kredittkort til én lavere rente (Eff. 11,46 % o/5 år)',
      status: 'active',
      clicks: 0,
      spendNok: 0,
      cpcNok: 0,
      lenderClickouts: 0,
      clickoutRatePct: 0,
    },
    {
      id: 'var-flow-2step',
      label: 'Variant C · 2-Step Debt-Count Micro-Wizard',
      track: 'Funnel Flow',
      destinationPath: '/lanekalkulator/?flow=2step',
      creativeHook: 'Steg 1: Velg beløp & antall smålån → Steg 2: Se dine 3 beste banker',
      status: 'queued_next',
      clicks: 0,
      spendNok: 0,
      cpcNok: 0,
      lenderClickouts: 0,
      clickoutRatePct: 0,
    },
  ],
  metaCreatives: [],
  verdictNote: 'Ideation muted while collecting sample (0 / 500 clicks). Google & Microsoft RSA proposals are snoozed.',
};

function formatTimestampCph(isoStr: string): { relative: string; exact: string } {
  try {
    const dt = new Date(isoStr);
    if (isNaN(dt.getTime())) return { relative: isoStr, exact: isoStr };
    const diffMs = Date.now() - dt.getTime();
    const diffMin = Math.round(diffMs / 60000);
    let relative = '';
    if (diffMin < -60) {
      const hrs = Math.round(Math.abs(diffMin) / 60);
      relative = `In ${hrs}h`;
    } else if (diffMin < 0) {
      relative = `In ${Math.abs(diffMin)}m`;
    } else if (diffMin < 2) {
      relative = 'Just now';
    } else if (diffMin < 60) {
      relative = `${diffMin}m ago`;
    } else if (diffMin < 24 * 60) {
      relative = `${Math.floor(diffMin / 60)}h ago`;
    } else {
      const days = Math.floor(diffMin / (24 * 60));
      relative = `${days}d ago`;
    }
    const exact = dt.toLocaleString('sv-SE', {
      timeZone: 'Europe/Copenhagen',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    return { relative, exact };
  } catch {
    return { relative: isoStr, exact: isoStr };
  }
}

export default function DailyOverviewPage() {
  const [data, setData] = useState<DailyOverviewData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [actionBanner, setActionBanner] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [busyActionKey, setBusyActionKey] = useState<string | null>(null);

  // Box 1: Active Experiment inline editor & add-variant states
  const [isEditingExpRules, setIsEditingExpRules] = useState<boolean>(false);
  const [targetClicksInput, setTargetClicksInput] = useState<number>(500);
  const [stopLossInput, setStopLossInput] = useState<string>('');
  const [verdictNoteInput, setVerdictNoteInput] = useState<string>('');
  const [savingExp, setSavingExp] = useState<boolean>(false);

  const [isAddingVariant, setIsAddingVariant] = useState<boolean>(false);
  const [newVarLabel, setNewVarLabel] = useState<string>('');
  const [newVarTrack, setNewVarTrack] = useState<string>('Landing Page');
  const [newVarPath, setNewVarPath] = useState<string>('/lanekalkulator/');
  const [newVarHook, setNewVarHook] = useState<string>('');

  // Box 3: Seed QA scan state
  const [syncingSendoutQa, setSyncingSendoutQa] = useState<boolean>(false);
  const [showAllSeedQa, setShowAllSeedQa] = useState<boolean>(false);

  // Optional Bottom Drawer (Collapsed by default so there is zero clutter)
  const [showBottomDrawer, setShowBottomDrawer] = useState<boolean>(false);
  const [drawerTab, setDrawerTab] = useState<'activity_log' | 'competitor_inbox' | 'ask_agent'>('activity_log');
  const [domainFilter, setDomainFilter] = useState<AgentDomain>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [syncingCompetitors, setSyncingCompetitors] = useState<boolean>(false);

  // On-demand single-agent question (only runs when explicitly clicked)
  const [askSpeakerId, setAskSpeakerId] = useState<'fox' | 'eskild' | 'bruun' | 'frederik' | 'amalie'>('fox');
  const [askPromptInput, setAskPromptInput] = useState<string>('');
  const [askingAgent, setAskingAgent] = useState<boolean>(false);

  // AI Budget Modal state
  const [isAiRosterOpen, setIsAiRosterOpen] = useState<boolean>(false);
  const [softCapInput, setSoftCapInput] = useState<number>(100);
  const [hardCapInput, setHardCapInput] = useState<number>(125);
  const [notifyEmailInput, setNotifyEmailInput] = useState<string>('kristoffer@afima.dk');
  const [slackWebhookInput, setSlackWebhookInput] = useState<string>('');
  const [testingSlack, setTestingSlack] = useState<boolean>(false);
  const [slackTestBanner, setSlackTestBanner] = useState<string | null>(null);
  const [savingBudget, setSavingBudget] = useState<boolean>(false);

  const loadData = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    try {
      const overview = await fetchDailyOverview();
      setData(overview);
      if (overview.aiBudget) {
        setSoftCapInput(overview.aiBudget.softCapUsd || 100);
        setHardCapInput(overview.aiBudget.hardCapUsd || 125);
        setNotifyEmailInput(overview.aiBudget.notifyEmail || 'kristoffer@afima.dk');
        setSlackWebhookInput(overview.aiBudget.slackWebhookUrl || '');
      }
      const exp = overview.soloOperatorView?.activeExperiments?.[0];
      if (exp) {
        setTargetClicksInput(exp.targetClicks || 500);
        setStopLossInput(exp.stopLossRule || '');
        setVerdictNoteInput(exp.verdictNote || '');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData(false);
  }, [loadData]);

  const activeExperiment: SoloExperiment = useMemo(() => {
    const fromBackend = data?.soloOperatorView?.activeExperiments?.[0];
    return fromBackend || FALLBACK_EXPERIMENT;
  }, [data]);

  const handleOneClickAction = async (actionType: string, targetId: string, keyLabel?: string) => {
    const busyKey = keyLabel || `${actionType}:${targetId}`;
    if (busyActionKey) return;
    setBusyActionKey(busyKey);
    setActionBanner(null);
    setErrorBanner(null);
    try {
      const res = await executeManagerAction(actionType, targetId);
      if (res.executionResult?.message) {
        setActionBanner(res.executionResult.message);
      }
      if (res.soloOperatorView && data) {
        setData({
          ...data,
          soloOperatorView: res.soloOperatorView,
          agentReflections: res.agentReflections || data.agentReflections,
        });
      } else {
        await loadData(true);
      }
    } catch (err: any) {
      setErrorBanner(err?.message || 'Action execution failed');
    } finally {
      setBusyActionKey(null);
    }
  };

  const handleSaveExperimentRules = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data || savingExp) return;
    setSavingExp(true);
    setErrorBanner(null);
    try {
      const res = await updateSoloExperiment({
        id: activeExperiment.id,
        targetClicks: Number(targetClicksInput) || 500,
        stopLossRule: stopLossInput.trim(),
        verdictNote: verdictNoteInput.trim(),
      });
      if (res.soloOperatorView) {
        setData({ ...data, soloOperatorView: res.soloOperatorView });
      }
      setIsEditingExpRules(false);
      setActionBanner('Updated experiment sample target & stop-loss rules.');
    } catch (err: any) {
      setErrorBanner(err?.message || 'Could not update experiment rules');
    } finally {
      setSavingExp(false);
    }
  };

  const handleAddVariant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data || !newVarLabel.trim() || savingExp) return;
    setSavingExp(true);
    setErrorBanner(null);
    try {
      const newVar: SoloExperimentVariant = {
        id: `var-${Date.now()}`,
        label: newVarLabel.trim(),
        track: newVarTrack.trim() || 'Landing Page',
        destinationPath: newVarPath.trim() || '/lanekalkulator/',
        creativeHook: newVarHook.trim() || 'New split-test variant',
        status: 'active',
        clicks: 0,
        spendNok: 0,
        cpcNok: 0,
        lenderClickouts: 0,
        clickoutRatePct: 0,
      };
      const nextVariants = [...(activeExperiment.variants || []), newVar];
      const res = await updateSoloExperiment({
        id: activeExperiment.id,
        variants: nextVariants,
      });
      if (res.soloOperatorView) {
        setData({ ...data, soloOperatorView: res.soloOperatorView });
      }
      setIsAddingVariant(false);
      setNewVarLabel('');
      setNewVarHook('');
      setActionBanner(`Added test variant "${newVar.label}".`);
    } catch (err: any) {
      setErrorBanner(err?.message || 'Could not add variant');
    } finally {
      setSavingExp(false);
    }
  };

  const handleSyncSendoutQa = async () => {
    if (!data || syncingSendoutQa) return;
    setSyncingSendoutQa(true);
    setErrorBanner(null);
    try {
      const res = await syncSendoutQaWatch(12);
      const refreshed = await fetchDailyOverview();
      setData({
        ...refreshed,
        sendoutQaWatch: res.sendoutQaWatch || refreshed.sendoutQaWatch,
      });
      if (res.syncResult) {
        setActionBanner(
          `Seed QA scanned ${res.syncResult.auditedCount} new sendouts (${res.syncResult.flaggedCount} flagged).`
        );
      }
    } catch (err: any) {
      setErrorBanner(err?.message || 'Failed running Seed Sendout QA');
    } finally {
      setSyncingSendoutQa(false);
    }
  };

  const handleSyncCompetitorInbox = async () => {
    if (!data || syncingCompetitors) return;
    setSyncingCompetitors(true);
    try {
      const res = await syncCompetitorInboxWatch(45);
      if (res.competitorWatch) {
        setData({ ...data, competitorWatch: res.competitorWatch });
      }
      if (res.syncResult) {
        setActionBanner(
          `Synced ${res.syncResult.emailAccount}: +${res.syncResult.insertedNewEmails} new competitor emails.`
        );
      }
    } catch (err: any) {
      setErrorBanner(err?.message || 'Failed syncing competitor inbox');
    } finally {
      setSyncingCompetitors(false);
    }
  };

  const handleAskAgentOnDemand = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data || !askPromptInput.trim() || askingAgent) return;
    setAskingAgent(true);
    setErrorBanner(null);
    try {
      const res = await triggerAgentReflection(askSpeakerId, askPromptInput.trim());
      setData({
        ...data,
        agentReflections: res.agentReflections || data.agentReflections,
        aiBudget: res.aiBudget || data.aiBudget,
      });
      setAskPromptInput('');
      setActionBanner(`Generated on-demand response from @${askSpeakerId.toUpperCase()}.`);
    } catch (err: any) {
      setErrorBanner(err?.message || 'Failed running on-demand agent query');
    } finally {
      setAskingAgent(false);
    }
  };

  const handleSaveBudgetSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setSavingBudget(true);
    try {
      const res = await updateAiBudgetSettings({
        softCapUsd: Number(softCapInput) || 100,
        hardCapUsd: Number(hardCapInput) || 125,
        notifyEmail: notifyEmailInput.trim() || 'kristoffer@afima.dk',
        notifyOnNeedsHuman: true,
        notifyOnDailyDigest: false,
        slackWebhookUrl: slackWebhookInput.trim(),
        notifySlackOnUrgent: true,
      });
      if (res.aiBudget) {
        setData({ ...data, aiBudget: res.aiBudget });
      }
      setIsAiRosterOpen(false);
    } finally {
      setSavingBudget(false);
    }
  };

  const handleTestSlackFromOverview = async () => {
    if (!data || testingSlack) return;
    setTestingSlack(true);
    setSlackTestBanner(null);
    try {
      const res = await sendTestSlackAlert(slackWebhookInput.trim() || undefined, 'amalie');
      if (res.slackResult?.sentSlack) {
        setSlackTestBanner('✓ Test Slack alert delivered!');
      } else {
        setSlackTestBanner(res.slackResult?.error || 'Could not send Slack alert.');
      }
    } catch (err: any) {
      setSlackTestBanner(err?.message || 'Failed testing Slack webhook');
    } finally {
      setTestingSlack(false);
    }
  };

  const filteredEntries = useMemo(() => {
    if (!data) return [];
    const q = searchQuery.trim().toLowerCase();
    return data.entries.filter((entry) => {
      if (domainFilter !== 'all' && entry.domain !== domainFilter) return false;
      if (q) {
        const hay = `${entry.title} ${entry.summary} ${entry.reasoning} ${entry.agentName} ${entry.geo}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, domainFilter, searchQuery]);

  if (loading && !data) {
    return (
      <MainLayout>
        <div className="max-w-7xl mx-auto py-16 flex flex-col items-center justify-center text-slate-500">
          <RefreshCw className="w-6 h-6 animate-spin text-[#0F3F34] mb-3" />
          <p className="text-sm font-medium text-slate-700">Loading Solo Operator Command Center...</p>
          <p className="text-xs text-slate-400 mt-1">
            Fetching Active Experiment Scoreboard, Ready-to-Publish Deliverables, and Autopilot Guardrails
          </p>
        </div>
      </MainLayout>
    );
  }

  if (!data) return null;

  const { pulse } = data;
  const solo = data.soloOperatorView;
  const seoDeliverables = solo?.deliverablesQueue?.seoArticles || [];
  const emailDeliverables = solo?.deliverablesQueue?.emailSlots || [];
  const adsDeliverables = solo?.deliverablesQueue?.adsProposals || [];
  const metaCreatives = activeExperiment.metaCreatives?.length
    ? activeExperiment.metaCreatives
    : solo?.deliverablesQueue?.metaCreatives || [];
  const totalDeliverablesCount = seoDeliverables.length + emailDeliverables.length + adsDeliverables.length;

  const autopilot = solo?.autopilotSummary || {
    negKeywordsApplied7d: 0,
    otherAdsActionsApplied7d: 0,
    suppressedAdsChannels: ['google', 'microsoft'],
    seoPublished7d: 0,
    openSeoAlerts: [],
  };

  const flaggedSeedAudits = (data.sendoutQaWatch?.recentAudits || []).filter(
    (a) => a.qa_status === 'critical' || a.qa_status === 'warning'
  );
  const lossBufferPct = Math.max(0, Math.min(100, Math.round((pulse.noAdsRemainingLossBufferDkk / 20000) * 100)));

  return (
    <MainLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-14">
        {/* ─── Top Bar: Calm Solo Operator Header ───────────────────────────── */}
        <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-xs flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-[#0F3F34] text-white">
                <Sliders className="w-3.5 h-3.5" />
                Solo Operator Command Center
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-900 border border-emerald-200">
                <Lock className="w-3 h-3 text-emerald-700" />
                Unprompted AI Essay Chatter: Muted ($0/day)
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              1 Active Experiment · 1-Click Deliverables · Quiet Autopilot
            </h2>
            <p className="text-xs sm:text-sm text-slate-500">
              Built for one operator: no daily agent essays or fake meetings — only live test scoreboards, finished drafts ready to publish, and exception guardrails.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            <button
              type="button"
              onClick={() => setIsAiRosterOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold border border-emerald-900/20 bg-[#F4F8F6] text-[#0F3F34] hover:bg-emerald-100/60 transition cursor-pointer"
            >
              <Cpu className="w-3.5 h-3.5 text-[#0F3F34]" />
              <span>
                AI Cap: <strong className="tabular-nums">${(data.aiBudget?.mtdBedrockUsd ?? 0).toFixed(2)}</strong> / $
                {Math.round(data.aiBudget?.softCapUsd ?? 100)}
              </span>
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 transition"
            >
              <TrendingUp className="w-3.5 h-3.5 text-[#0F3F34]" />
              WCT Revenue
              <ArrowUpRight className="w-3.5 h-3.5 text-slate-400" />
            </Link>
            <button
              type="button"
              onClick={() => loadData(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold bg-[#0F3F34] text-white hover:bg-[#0b3027] disabled:opacity-50 transition cursor-pointer"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Feedback Banners */}
        {actionBanner && (
          <div className="px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-950 flex items-center justify-between">
            <span>✓ {actionBanner}</span>
            <button type="button" onClick={() => setActionBanner(null)} className="text-emerald-700 hover:text-emerald-950 font-bold cursor-pointer">
              ✕
            </button>
          </div>
        )}
        {errorBanner && (
          <div className="px-4 py-3 rounded-xl bg-rose-50 border border-rose-200 text-xs font-semibold text-rose-900 flex items-center justify-between">
            <span>⚠️ {errorBanner}</span>
            <button type="button" onClick={() => setErrorBanner(null)} className="text-rose-700 hover:text-rose-950 font-bold cursor-pointer">
              ✕
            </button>
          </div>
        )}

        {/* ─── Compact 4-Metric Pulse Strip ─────────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">WCT Commission Revenue</div>
            <div className="text-lg font-bold text-slate-900 tabular-nums mt-1">
              {pulse.revenueTodayDkk > 0
                ? `${Math.round(pulse.revenueTodayDkk).toLocaleString()} DKK Today`
                : pulse.revenueYesterdayDkk > 0
                ? `${Math.round(pulse.revenueYesterdayDkk).toLocaleString()} DKK (Yest.)`
                : `${Math.round(pulse.revenue7dDkk).toLocaleString()} DKK (7d)`}
            </div>
            <div className="text-xs text-slate-500 mt-1 tabular-nums">
              Trailing 7d: <strong className="text-slate-700">{Math.round(pulse.revenue7dDkk).toLocaleString()} DKK</strong>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Paid Ads Loss Buffer</span>
              <span className="text-[11px] font-bold text-[#0F3F34] tabular-nums">{lossBufferPct}% intact</span>
            </div>
            <div className="text-lg font-bold text-slate-900 tabular-nums mt-1">
              {Math.round(pulse.noAdsRemainingLossBufferDkk).toLocaleString()} / 20,000 DKK
            </div>
            <div className="text-xs text-slate-500 mt-1 tabular-nums">
              Daily Cap: <strong className="text-slate-700">{Math.round(pulse.noAdsTodayCapDkk).toLocaleString()} DKK/d</strong> · MTD Spend: {Math.round(pulse.noAdsMtdSpendDkk).toLocaleString()} DKK
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Email SendEngine Today</div>
            <div className="text-lg font-bold text-slate-900 tabular-nums mt-1">
              {pulse.emailSentToday} Sent · {pulse.emailScheduledToday} Scheduled
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {pulse.emailHeldToday > 0 ? (
                <span className="text-amber-700 font-semibold">{pulse.emailHeldToday} Held slot(s) in Box 2</span>
              ) : (
                <span className="text-emerald-700 font-medium">0 held slots · All ESPs healthy</span>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200/90 p-4 shadow-xs">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">SEO Publish Buffer</div>
            <div className="text-lg font-bold text-slate-900 tabular-nums mt-1">
              🇸🇪 {pulse.seoBufferDaysSe}d · 🇳🇴 {pulse.seoBufferDaysNo}d
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {seoDeliverables.length > 0 ? (
                <span className="text-slate-700 font-medium">{seoDeliverables.length} ready/review draft(s) in Box 2</span>
              ) : (
                <span>Auto-publishing 1.5 articles/day</span>
              )}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            BOX 1: ACTIVE EXPERIMENT SCOREBOARD ("ONE ACTIVE BET AT A TIME")
           ═══════════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
          <div className="bg-gradient-to-r from-[#0F3F34] via-emerald-950 to-slate-900 text-white px-5 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[#F07824] text-white">
                  BOX 1 · ACTIVE EXPERIMENT SCOREBOARD
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-white/10 text-emerald-200 border border-white/15">
                  {GEO_FLAGS[activeExperiment.geo] || activeExperiment.geo} · {activeExperiment.channel}
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-mono bg-emerald-500/20 text-emerald-200 border border-emerald-400/30">
                  {activeExperiment.startsAt} → {activeExperiment.endsAt}
                </span>
                {(activeExperiment.snoozedChannels || []).map((sc, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-500/25 text-rose-200 border border-rose-400/30"
                  >
                    🔕 {sc}
                  </span>
                ))}
              </div>
              <h3 className="text-base sm:text-lg font-bold text-white">{activeExperiment.title}</h3>
            </div>

            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <button
                type="button"
                onClick={() => setIsAddingVariant(!isAddingVariant)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/20 transition cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                Add Test Variant
              </button>
              <button
                type="button"
                onClick={() => setIsEditingExpRules(!isEditingExpRules)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/20 text-white border border-white/20 transition cursor-pointer"
              >
                <Edit3 className="w-3.5 h-3.5" />
                Edit Target / Stop-Loss
              </button>
              <Link
                to="/simplyloan/optimizer"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#F07824] text-white hover:opacity-95 transition"
              >
                Ads Optimizer
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {/* Sample Progress Bar + Guardrail Pills */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center bg-slate-50 rounded-xl p-4 border border-slate-200/80">
              <div className="lg:col-span-5 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-800">
                    Sample Collection Progress (No Ideation Until Target Reached)
                  </span>
                  <span className="font-mono font-bold text-[#0F3F34]">
                    {activeExperiment.currentClicks} / {activeExperiment.targetClicks} clicks ({activeExperiment.progressPct}%)
                  </span>
                </div>
                <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#0F3F34] rounded-full transition-all"
                    style={{ width: `${Math.max(3, activeExperiment.progressPct)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>Sprint Spend: <strong className="text-slate-700">{activeExperiment.currentSpendNok.toLocaleString()} NOK</strong></span>
                  <span>Status: <strong className="text-emerald-800 uppercase">{activeExperiment.status.replace(/_/g, ' ')}</strong></span>
                </div>
              </div>

              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-lg bg-white border border-slate-200/90 space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700">Stop-Loss Rule</div>
                  <p className="text-slate-700 leading-snug">{activeExperiment.stopLossRule}</p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-slate-200/90 space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Winner Criteria</div>
                  <p className="text-slate-700 leading-snug">{activeExperiment.successCriteria}</p>
                </div>
              </div>
            </div>

            {/* Optional Edit Target / Stop-Loss Form */}
            {isEditingExpRules && (
              <form onSubmit={handleSaveExperimentRules} className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Target Sample Size (Clicks)</label>
                    <input
                      type="number"
                      value={targetClicksInput}
                      onChange={(e) => setTargetClicksInput(Number(e.target.value))}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Stop-Loss Guardrail Rule</label>
                    <input
                      type="text"
                      value={stopLossInput}
                      onChange={(e) => setStopLossInput(e.target.value)}
                      className="w-full px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <input
                    type="text"
                    placeholder="Status / Verdict note..."
                    value={verdictNoteInput}
                    onChange={(e) => setVerdictNoteInput(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setIsEditingExpRules(false)}
                      className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-lg cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={savingExp}
                      className="px-3.5 py-1.5 text-xs font-semibold bg-[#0F3F34] text-white rounded-lg hover:bg-[#0b3027] cursor-pointer"
                    >
                      {savingExp ? 'Saving...' : 'Save Rules'}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Optional Add Variant Form */}
            {isAddingVariant && (
              <form onSubmit={handleAddVariant} className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <div className="text-xs font-bold text-slate-800">Add Landing Page or Funnel Flow Variant to Sprint</div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5">
                  <input
                    type="text"
                    placeholder="Variant Name (e.g. Variant D · 3-Bank Pre-Lander)"
                    value={newVarLabel}
                    onChange={(e) => setNewVarLabel(e.target.value)}
                    required
                    className="sm:col-span-2 px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Track (e.g. Landing Page)"
                    value={newVarTrack}
                    onChange={(e) => setNewVarTrack(e.target.value)}
                    className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                  />
                  <input
                    type="text"
                    placeholder="Path (e.g. /lanekalkulator/)"
                    value={newVarPath}
                    onChange={(e) => setNewVarPath(e.target.value)}
                    className="px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg font-mono"
                  />
                </div>
                <div className="flex items-center justify-between gap-2.5">
                  <input
                    type="text"
                    placeholder="Hook / Hypothesis being tested..."
                    value={newVarHook}
                    onChange={(e) => setNewVarHook(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setIsAddingVariant(false)}
                      className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-lg cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={savingExp}
                      className="px-3.5 py-1.5 text-xs font-semibold bg-[#F07824] text-white rounded-lg hover:opacity-95 cursor-pointer"
                    >
                      Add Variant
                    </button>
                  </div>
                </div>
              </form>
            )}

            {/* Variants Split-Test Table */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Landing Page &amp; Funnel Variants in Flight ({(activeExperiment.variants || []).length})
                </h4>
                <span className="text-[11px] text-slate-500">{activeExperiment.verdictNote}</span>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      <th className="py-2.5 px-3.5">Variant &amp; Hook</th>
                      <th className="py-2.5 px-3">Destination Route</th>
                      <th className="py-2.5 px-3 text-right">Clicks</th>
                      <th className="py-2.5 px-3 text-right">Spend / CPC</th>
                      <th className="py-2.5 px-3 text-right">/slgo/ Click-outs</th>
                      <th className="py-2.5 px-3">Status</th>
                      <th className="py-2.5 px-3.5 text-right">1-Click Control</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/80">
                    {(activeExperiment.variants || []).map((v) => {
                      const isWinner = v.status === 'winner';
                      const isActive = v.status === 'active';
                      const isPaused = v.status === 'paused';
                      return (
                        <tr key={v.id} className={isWinner ? 'bg-emerald-50/50' : 'hover:bg-slate-50/70'}>
                          <td className="py-3 px-3.5">
                            <div className="font-bold text-slate-900 flex items-center gap-1.5">
                              <span>{v.label}</span>
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                {v.track}
                              </span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">{v.creativeHook}</div>
                          </td>
                          <td className="py-3 px-3 font-mono text-[11px] text-[#0F3F34] font-semibold">
                            {v.destinationPath}
                          </td>
                          <td className="py-3 px-3 text-right font-mono font-semibold text-slate-800">
                            {v.clicks.toLocaleString()}
                          </td>
                          <td className="py-3 px-3 text-right font-mono text-slate-700">
                            {v.spendNok > 0 ? `${v.spendNok.toFixed(0)} NOK (${v.cpcNok.toFixed(2)} kr)` : '0 NOK'}
                          </td>
                          <td className="py-3 px-3 text-right font-mono font-semibold text-slate-800">
                            {v.lenderClickouts} ({v.clickoutRatePct.toFixed(1)}%)
                          </td>
                          <td className="py-3 px-3">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                isWinner
                                  ? 'bg-emerald-600 text-white'
                                  : isActive
                                  ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                                  : isPaused
                                  ? 'bg-slate-200 text-slate-700'
                                  : 'bg-amber-50 text-amber-900 border border-amber-200'
                              }`}
                            >
                              {v.status.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="py-3 px-3.5 text-right">
                            <div className="inline-flex items-center gap-1">
                              {!isActive && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOneClickAction(
                                      'update_experiment_variant',
                                      `${activeExperiment.id}:${v.id}:active`
                                    )
                                  }
                                  className="px-2 py-1 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-900 border border-emerald-200 hover:bg-emerald-100 cursor-pointer inline-flex items-center gap-1"
                                  title="Activate Variant"
                                >
                                  <Play className="w-3 h-3" />
                                  Activate
                                </button>
                              )}
                              {isActive && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOneClickAction(
                                      'update_experiment_variant',
                                      `${activeExperiment.id}:${v.id}:paused`
                                    )
                                  }
                                  className="px-2 py-1 rounded text-[11px] font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 cursor-pointer inline-flex items-center gap-1"
                                  title="Pause Variant"
                                >
                                  <Pause className="w-3 h-3" />
                                  Pause
                                </button>
                              )}
                              {!isWinner && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleOneClickAction(
                                      'update_experiment_variant',
                                      `${activeExperiment.id}:${v.id}:winner`
                                    )
                                  }
                                  className="px-2 py-1 rounded text-[11px] font-semibold bg-amber-50 text-amber-900 border border-amber-200 hover:bg-amber-100 cursor-pointer inline-flex items-center gap-1"
                                  title="Mark as Winner"
                                >
                                  <Award className="w-3 h-3" />
                                  Winner
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Rendered Meta Ad Creatives Attached to Sprint */}
            {metaCreatives.length > 0 && (
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Rendered Meta Ad Creatives Ready for Sprint ({metaCreatives.length})
                  </h4>
                  <span className="text-[11px] text-emerald-800 font-medium">
                    ✓ All creatives include statutory Forskrift § 5 representative price example
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {metaCreatives.slice(0, 6).map((mc) => {
                    const isAct = mc.status === 'active';
                    return (
                      <div
                        key={mc.id}
                        className="rounded-xl border border-slate-200/90 bg-slate-50/60 p-3.5 flex flex-col justify-between gap-2.5"
                      >
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-white border border-slate-200 text-slate-700">
                              {mc.assetKey} ({mc.sizeLabel})
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                isAct
                                  ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                                  : 'bg-slate-200 text-slate-700'
                              }`}
                            >
                              {mc.status}
                            </span>
                          </div>
                          <div className="text-xs font-bold text-slate-900">{mc.headline}</div>
                          {mc.primaryText && (
                            <p className="text-[11px] text-slate-600 line-clamp-2 leading-relaxed">{mc.primaryText}</p>
                          )}
                        </div>
                        <div className="pt-2 border-t border-slate-200/70 flex items-center justify-between gap-2">
                          <span className="text-[10px] text-emerald-800 font-medium">§ 5 Legal Footer ✓</span>
                          <button
                            type="button"
                            onClick={() => handleOneClickAction('toggle_meta_creative', String(mc.id))}
                            className={`px-2.5 py-1 rounded text-[11px] font-semibold transition cursor-pointer ${
                              isAct
                                ? 'bg-slate-200 text-slate-800 hover:bg-slate-300'
                                : 'bg-[#0F3F34] text-white hover:bg-[#0b3027]'
                            }`}
                          >
                            {isAct ? 'Pause Creative' : 'Set Active'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            BOX 2: READY-TO-PUBLISH DELIVERABLES QUEUE ("FINISHED ASSETS ONLY")
           ═══════════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
          <div className="bg-slate-900 text-white px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500 text-slate-950">
                  BOX 2 · READY-TO-PUBLISH DELIVERABLES ({totalDeliverablesCount})
                </span>
                <span className="text-xs text-slate-300">
                  · Finished drafts &amp; scheduled slots waiting for your 1-click approval (zero vague ideas)
                </span>
              </div>
              <h3 className="text-base font-bold">
                1-Click Execution Queue — SEO Articles, Email Slots &amp; On-Focus Ad Changes
              </h3>
            </div>
          </div>

          <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Column 1: SEO Articles Ready */}
            <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50/40 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#0F3F34]" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-800">
                      SEO Articles ({seoDeliverables.length})
                    </span>
                  </div>
                  <Link to="/simplyloan/seo" className="text-[11px] font-semibold text-[#0F3F34] hover:underline">
                    SEO Hub →
                  </Link>
                </div>

                {seoDeliverables.length === 0 ? (
                  <div className="py-6 text-center text-xs text-slate-500 space-y-1">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" />
                    <p className="font-semibold text-slate-700">All drafted articles are queued</p>
                    <p className="text-[11px]">Buffer: SE {pulse.seoBufferDaysSe}d · NO {pulse.seoBufferDaysNo}d</p>
                  </div>
                ) : (
                  <div className="space-y-2.5 max-h-[340px] overflow-y-auto pr-1">
                    {seoDeliverables.map((art) => (
                      <div key={art.id} className="p-3 rounded-xl bg-white border border-slate-200/90 space-y-2">
                        <div className="flex items-center justify-between gap-1.5">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-800">
                            {GEO_FLAGS[art.market] || art.market} · {art.status === 'review' ? 'Needs Approval' : 'Ready Buffer'}
                          </span>
                          <span className="text-[10px] font-mono text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                            Gate {art.qualityScore}/100 · {art.wordCount}w
                          </span>
                        </div>
                        <div className="text-xs font-bold text-slate-900 leading-snug">{art.title}</div>
                        <div className="text-[11px] text-slate-500">
                          Keyword: <strong className="text-slate-700">«{art.targetKeyword}»</strong>
                        </div>
                        <div className="pt-1 flex items-center justify-between gap-2">
                          {art.status === 'review' ? (
                            <button
                              type="button"
                              onClick={() => handleOneClickAction('promote_seo_article', String(art.id))}
                              className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#0F3F34] text-white hover:bg-[#0b3027] transition cursor-pointer inline-flex items-center gap-1"
                            >
                              <Check className="w-3 h-3" />
                              Approve to Publish Queue
                            </button>
                          ) : (
                            <span className="text-[11px] font-semibold text-emerald-700">✓ Queued in Auto-Publisher</span>
                          )}
                          <Link
                            to="/simplyloan/seo"
                            className="text-[11px] font-semibold text-slate-600 hover:text-[#0F3F34]"
                          >
                            Preview →
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Column 2: Email Sendout Slots Requiring Call */}
            <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50/40 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[#0F3F34]" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-800">
                      Held / Pending Email Slots ({emailDeliverables.length})
                    </span>
                  </div>
                  <Link to="/email/auto-schedule" className="text-[11px] font-semibold text-[#0F3F34] hover:underline">
                    Auto-Schedule →
                  </Link>
                </div>

                {emailDeliverables.length === 0 ? (
                  <div className="py-6 text-center text-xs text-slate-500 space-y-1">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" />
                    <p className="font-semibold text-slate-700">0 Held Email Slots Today</p>
                    <p className="text-[11px]">
                      {pulse.emailSentToday} sent · {pulse.emailScheduledToday} scheduled automatically
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2.5 max-h-[340px] overflow-y-auto pr-1">
                    {emailDeliverables.map((slot, idx) => (
                      <div key={slot.afimaCampaignId || idx} className="p-3 rounded-xl bg-white border border-slate-200/90 space-y-2">
                        <div className="flex items-center justify-between gap-1.5">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-900 border border-amber-200 uppercase">
                            {GEO_FLAGS[slot.geo] || slot.geo} · {slot.rawStatus}
                          </span>
                          <span className="text-[10px] font-mono text-slate-600">
                            Score {Math.round(slot.score)}/100
                          </span>
                        </div>
                        <div className="text-xs font-bold text-slate-900 leading-snug">{slot.title}</div>
                        <p className="text-[11px] text-slate-600 line-clamp-2">{slot.reasoning}</p>
                        <div className="pt-1 flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => handleOneClickAction('approve_email_campaign', slot.afimaCampaignId)}
                            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#0F3F34] text-white hover:bg-[#0b3027] transition cursor-pointer inline-flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" />
                            Approve &amp; Schedule
                          </button>
                          <Link
                            to="/email/auto-schedule"
                            className="text-[11px] font-semibold text-slate-600 hover:text-[#0F3F34]"
                          >
                            Inspect →
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Column 3: On-Focus Paid Ads Proposals */}
            <div className="rounded-xl border border-slate-200 p-4 space-y-3 bg-slate-50/40 flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-slate-200 pb-2.5">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-[#0F3F34]" />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-800">
                      Paid Ads Queue ({adsDeliverables.length})
                    </span>
                  </div>
                  <Link to="/simplyloan/optimizer" className="text-[11px] font-semibold text-[#0F3F34] hover:underline">
                    Optimizer →
                  </Link>
                </div>

                {adsDeliverables.length === 0 ? (
                  <div className="py-6 text-center text-xs text-slate-500 space-y-1">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mx-auto" />
                    <p className="font-semibold text-slate-700">Inbox Zero on Paid Ads</p>
                    <p className="text-[11px]">
                      Google &amp; Microsoft RSA proposals snoozed during 2-week Meta Sprint. Negative keywords auto-applied.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2.5 max-h-[340px] overflow-y-auto pr-1">
                    {adsDeliverables.map((prop) => (
                      <div key={prop.id} className="p-3 rounded-xl bg-white border border-slate-200/90 space-y-2">
                        <div className="flex items-center justify-between gap-1.5">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-800 uppercase">
                            {GEO_FLAGS[prop.geo] || prop.geo} · {prop.platform}
                          </span>
                          {prop.impactDkk !== 0 && (
                            <span className="text-[10px] font-mono font-bold text-emerald-800">
                              {prop.impactDkk > 0 ? '+' : ''}
                              {Math.round(prop.impactDkk)} DKK/d
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-bold text-slate-900 leading-snug">{prop.title}</div>
                        <p className="text-[11px] text-slate-600 line-clamp-2">{prop.rationale}</p>
                        <div className="pt-1 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleOneClickAction('approve_ads_proposal', String(prop.id))}
                            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-[#0F3F34] text-white hover:bg-[#0b3027] transition cursor-pointer inline-flex items-center gap-1"
                          >
                            <Check className="w-3 h-3" />
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOneClickAction('dismiss_ads_proposal', String(prop.id))}
                            className="px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition cursor-pointer"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            BOX 3: DAILY AUTOPILOT & EXCEPTION WATCHDOG ("SILENT GUARDRAILS")
           ═══════════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-slate-50/70">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#0F3F34]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#0F3F34]">
                  BOX 3 · DAILY AUTOPILOT &amp; EXCEPTION LOG
                </span>
              </div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900 mt-0.5">
                Routine Daily Adjustments Handled Quietly in Background (Only Highlights Exceptions)
              </h3>
            </div>
            <button
              type="button"
              disabled={syncingSendoutQa}
              onClick={handleSyncSendoutQa}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 cursor-pointer self-start sm:self-center"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncingSendoutQa ? 'animate-spin' : ''}`} />
              {syncingSendoutQa ? 'Scanning Seed Inbox...' : 'Scan Seed Sendouts Now'}
            </button>
          </div>

          <div className="divide-y divide-slate-200/80 text-xs">
            {/* Row 1: Paid Ads Daily Search Hygiene & P&L Solver */}
            <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-slate-900">
                    Paid Ads Daily Search Hygiene &amp; P&amp;L Guardrail (Every 4h / 24h)
                  </div>
                  <p className="text-slate-600 mt-0.5">
                    Auto-applied <strong>{autopilot.negKeywordsApplied7d} negative keywords</strong> and{' '}
                    <strong>{autopilot.otherAdsActionsApplied7d} budget/bid adjustments</strong> (last 7d). Loss buffer at{' '}
                    <strong>{Math.round(pulse.noAdsRemainingLossBufferDkk).toLocaleString()} DKK</strong> ({lossBufferPct}%).
                    Off-sprint channels ({autopilot.suppressedAdsChannels.map((c) => c.toUpperCase()).join(', ')}) snoozed.
                  </p>
                </div>
              </div>
              <Link
                to="/simplyloan/optimizer"
                className="text-xs font-semibold text-[#0F3F34] hover:underline shrink-0"
              >
                Inspect Rules →
              </Link>
            </div>

            {/* Row 2: Own Sendout Seed Inbox QA */}
            <div className="p-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  {flaggedSeedAudits.length > 0 ? (
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <div className="font-bold text-slate-900">
                      Own Sendout Seed QA Watchdog (lars.hedlund84@gmail.com · Every 2h)
                    </div>
                    <p className="text-slate-600 mt-0.5">
                      Audited <strong>{data.sendoutQaWatch?.totalAudited || 0}</strong> live sendouts:{' '}
                      <strong className="text-emerald-700">{data.sendoutQaWatch?.passCount || 0} Passed</strong> ·{' '}
                      <strong className={flaggedSeedAudits.length > 0 ? 'text-amber-700' : 'text-slate-600'}>
                        {flaggedSeedAudits.length} Flagged
                      </strong>{' '}
                      (Inbox: {data.sendoutQaWatch?.inboxCount || 0}, Spam: {data.sendoutQaWatch?.spamCount || 0}).
                    </p>
                  </div>
                </div>
                {flaggedSeedAudits.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllSeedQa(!showAllSeedQa)}
                    className="text-xs font-semibold text-[#0F3F34] hover:underline shrink-0 cursor-pointer"
                  >
                    {showAllSeedQa ? 'Hide Flagged Sendouts' : `Inspect ${flaggedSeedAudits.length} Flagged Sendouts →`}
                  </button>
                )}
              </div>

              {showAllSeedQa && flaggedSeedAudits.length > 0 && (
                <div className="space-y-2 pl-7">
                  {flaggedSeedAudits.slice(0, 6).map((audit) => (
                    <div
                      key={audit.id}
                      className="p-3 rounded-xl border border-amber-200 bg-amber-50/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2"
                    >
                      <div className="space-y-0.5">
                        <div className="font-bold text-slate-900">
                          [{audit.qa_status.toUpperCase()}] {GEO_FLAGS[audit.geo] || audit.geo} · {audit.esp_detected} — &ldquo;{audit.subject}&rdquo;
                        </div>
                        <div className="text-[11px] text-slate-700">{audit.bruun_summary_da}</div>
                        {audit.suggested_fix && (
                          <div className="text-[11px] text-[#0F3F34] font-medium">Fix: {audit.suggested_fix}</div>
                        )}
                      </div>
                      <Link
                        to="/email/campaigns"
                        className="px-2.5 py-1 rounded bg-[#0F3F34] text-white text-[11px] font-semibold shrink-0"
                      >
                        Campaign Hub →
                      </Link>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Row 3: Email Deliverability & 90/10 Probation Pruner */}
            <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-bold text-slate-900">
                    Email Deliverability &amp; 90/10 Probation Auto-Pruner (Daily 01:30 UTC)
                  </div>
                  <p className="text-slate-600 mt-0.5">
                    90/10 Active-vs-Probation split active across all ESPs · 8d Last-Chance reactivation · 14d suspend · 30d auto-delete · Converter Protection ON.
                  </p>
                </div>
              </div>
              <Link
                to="/email/esp-dashboard"
                className="text-xs font-semibold text-[#0F3F34] hover:underline shrink-0"
              >
                ESP Health →
              </Link>
            </div>

            {/* Row 4: SEO Auto-Publisher & Open Alerts */}
            <div className="p-4 space-y-2.5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  {autopilot.openSeoAlerts.length > 0 ? (
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <div className="font-bold text-slate-900">
                      SEO Jittered Auto-Publisher &amp; Link Watchdog (Every 15m)
                    </div>
                    <p className="text-slate-600 mt-0.5">
                      Published <strong>{autopilot.seoPublished7d} articles</strong> in the last 7 days. Buffers: SE{' '}
                      <strong>{pulse.seoBufferDaysSe}d</strong> · NO <strong>{pulse.seoBufferDaysNo}d</strong>.{' '}
                      {autopilot.openSeoAlerts.length > 0
                        ? `${autopilot.openSeoAlerts.length} open alert(s) below.`
                        : '0 open SEO alerts.'}
                    </p>
                  </div>
                </div>
                <Link to="/simplyloan/seo" className="text-xs font-semibold text-[#0F3F34] hover:underline shrink-0">
                  SEO Hub →
                </Link>
              </div>

              {autopilot.openSeoAlerts.length > 0 && (
                <div className="space-y-1.5 pl-7">
                  {autopilot.openSeoAlerts.map((al) => (
                    <div
                      key={al.id}
                      className="p-2.5 rounded-lg bg-amber-50/60 border border-amber-200 flex items-center justify-between gap-2"
                    >
                      <span className="text-slate-800 font-medium">
                        <strong>[{GEO_FLAGS[al.market] || al.market}]</strong> {al.title}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleOneClickAction('acknowledge_seo_alert', String(al.id))}
                        className="px-2.5 py-1 rounded bg-white border border-slate-300 text-[11px] font-semibold text-slate-700 hover:bg-slate-100 cursor-pointer shrink-0"
                      >
                        Acknowledge
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════
            OPTIONAL COLLAPSED DRAWER: LOGS, COMPETITOR INBOX & ON-DEMAND PROMPT
           ═══════════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden">
          <button
            type="button"
            onClick={() => setShowBottomDrawer(!showBottomDrawer)}
            className="w-full px-5 py-3.5 flex items-center justify-between text-left hover:bg-slate-50 transition cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              {showBottomDrawer ? (
                <ChevronDown className="w-4 h-4 text-slate-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-500" />
              )}
              <span className="text-xs font-bold uppercase tracking-wider text-slate-600">
                Optional Tools &amp; Raw Logs (Collapsed to Keep Overview Clean)
              </span>
              <span className="text-[11px] text-slate-400">
                · Activity Log ({data.entries.length}) · Competitor Inbox Watch ({data.competitorWatch?.totalCompetitorEmails || 0}) · On-Demand Agent Query
              </span>
            </div>
            <span className="text-xs font-semibold text-[#0F3F34]">
              {showBottomDrawer ? 'Hide Drawer' : 'Expand Drawer'}
            </span>
          </button>

          {showBottomDrawer && (
            <div className="border-t border-slate-200 p-5 space-y-4">
              {/* Drawer Tabs */}
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
                <button
                  type="button"
                  onClick={() => setDrawerTab('activity_log')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${
                    drawerTab === 'activity_log'
                      ? 'bg-[#0F3F34] text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Activity &amp; Execution Log ({data.entries.length})
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerTab('competitor_inbox')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${
                    drawerTab === 'competitor_inbox'
                      ? 'bg-[#0F3F34] text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  CoReg Competitor Inbox Watch ({data.competitorWatch?.totalCompetitorEmails || 0})
                </button>
                <button
                  type="button"
                  onClick={() => setDrawerTab('ask_agent')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer ${
                    drawerTab === 'ask_agent'
                      ? 'bg-[#0F3F34] text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  Ask an Agent On-Demand
                </button>
              </div>

              {/* Tab 1: Raw Activity Log */}
              {drawerTab === 'activity_log' && (
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex flex-wrap gap-1.5">
                      {(['all', 'ads', 'seo', 'email', 'deliverability', 'workspace'] as AgentDomain[]).map((dom) => (
                        <button
                          key={dom}
                          type="button"
                          onClick={() => setDomainFilter(dom)}
                          className={`px-2.5 py-1 rounded text-xs font-semibold cursor-pointer ${
                            domainFilter === dom
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {dom.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <div className="relative w-full sm:w-64">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Filter log..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                    {filteredEntries.slice(0, 40).map((entry) => {
                      const time = formatTimestampCph(entry.timestamp);
                      return (
                        <div
                          key={entry.id}
                          className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                        >
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900">{entry.title}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600">
                                {entry.agentName}
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-600">{entry.summary}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 text-[11px] text-slate-500">
                            <span>{time.relative}</span>
                            <Link to={entry.deepLink} className="font-semibold text-[#0F3F34] hover:underline">
                              Open →
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tab 2: Competitor Inbox Watch */}
              {drawerTab === 'competitor_inbox' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-700">
                      Monitored Seed Inbox: <code className="font-mono">{data.competitorWatch?.monitoredInbox || 'chris.mads95@gmail.com'}</code>
                    </span>
                    <button
                      type="button"
                      disabled={syncingCompetitors}
                      onClick={handleSyncCompetitorInbox}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#0F3F34] text-white hover:bg-[#0b3027] disabled:opacity-50 cursor-pointer"
                    >
                      {syncingCompetitors ? 'Syncing...' : 'Sync Competitor Inbox'}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {(data.competitorWatch?.topOffersLeaderboard || []).slice(0, 9).map((item, idx) => (
                      <div key={idx} className="p-3 rounded-xl border border-slate-200 bg-slate-50/50 text-xs space-y-1">
                        <div className="flex items-center justify-between font-bold text-slate-900">
                          <span>{item.offer_domain}</span>
                          <span className="text-[11px] font-mono text-[#0F3F34]">{item.sends_count} sends</span>
                        </div>
                        <div className="text-[11px] text-slate-500">
                          Network: {item.affiliate_network} · Senders: {item.competitor_senders}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tab 3: On-Demand Single Agent Prompt */}
              {drawerTab === 'ask_agent' && (
                <div className="space-y-3">
                  <p className="text-xs text-slate-600">
                    Agents no longer generate automatic daily essays. If you want a specific analysis or draft on demand, ask below:
                  </p>
                  <form onSubmit={handleAskAgentOnDemand} className="flex flex-col sm:flex-row gap-2">
                    <select
                      value={askSpeakerId}
                      onChange={(e) => setAskSpeakerId(e.target.value as any)}
                      className="px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-slate-300"
                    >
                      <option value="fox">Fox · Paid Ads</option>
                      <option value="eskild">Eskild · SEO</option>
                      <option value="bruun">Bruun · Email</option>
                      <option value="frederik">Frederik · Deliverability</option>
                      <option value="amalie">Amalie · Chief of Staff</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Ask a specific question or request an analysis on demand..."
                      value={askPromptInput}
                      onChange={(e) => setAskPromptInput(e.target.value)}
                      className="flex-1 px-3 py-2 text-xs border border-slate-300 rounded-lg"
                    />
                    <button
                      type="submit"
                      disabled={askingAgent || !askPromptInput.trim()}
                      className="px-4 py-2 rounded-lg text-xs font-semibold bg-[#0F3F34] text-white hover:bg-[#0b3027] disabled:opacity-50 cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <Send className="w-3.5 h-3.5" />
                      {askingAgent ? 'Running...' : 'Ask Now'}
                    </button>
                  </form>

                  {(data.agentReflections || []).slice(0, 3).map((refl) => (
                    <div key={refl.id} className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/60 text-xs space-y-1.5">
                      <div className="flex items-center justify-between font-bold text-slate-900">
                        <span>{refl.speakerName} · {refl.lensTitle}</span>
                        <span className="text-[11px] font-normal text-slate-500">
                          {refl.createdAt ? formatTimestampCph(refl.createdAt).relative : ''}
                        </span>
                      </div>
                      <p className="text-slate-700 leading-relaxed">{refl.thoughtProcess}</p>
                      {refl.hypothesis && (
                        <div className="p-2 rounded bg-white border border-slate-200 text-slate-800">
                          <strong>Proposal:</strong> {refl.hypothesis}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* AI Budget & Slack Modal */}
        {isAiRosterOpen && (
          <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 shadow-xl">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <h3 className="text-base font-bold text-slate-900">AI Spend Cap &amp; Slack Exception Pager</h3>
                <button
                  type="button"
                  onClick={() => setIsAiRosterOpen(false)}
                  className="text-slate-400 hover:text-slate-600 cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <form onSubmit={handleSaveBudgetSettings} className="space-y-3 text-xs">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Monthly Soft Cap ($ USD)</label>
                    <input
                      type="number"
                      value={softCapInput}
                      onChange={(e) => setSoftCapInput(Number(e.target.value))}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block font-semibold text-slate-700 mb-1">Monthly Hard Cap ($ USD)</label>
                    <input
                      type="number"
                      value={hardCapInput}
                      onChange={(e) => setHardCapInput(Number(e.target.value))}
                      className="w-full px-3 py-1.5 border border-slate-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Slack Webhook URL (For Critical Alerts)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={slackWebhookInput}
                      onChange={(e) => setSlackWebhookInput(e.target.value)}
                      placeholder="https://hooks.slack.com/services/..."
                      className="flex-1 px-3 py-1.5 border border-slate-300 rounded-lg"
                    />
                    <button
                      type="button"
                      disabled={testingSlack}
                      onClick={handleTestSlackFromOverview}
                      className="px-3 py-1.5 rounded-lg font-semibold bg-slate-100 text-slate-800 hover:bg-slate-200 cursor-pointer"
                    >
                      {testingSlack ? 'Testing...' : 'Test'}
                    </button>
                  </div>
                  {slackTestBanner && <p className="text-[11px] text-emerald-800 mt-1">{slackTestBanner}</p>}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsAiRosterOpen(false)}
                    className="px-3 py-1.5 rounded-lg font-semibold text-slate-600 hover:bg-slate-100 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingBudget}
                    className="px-4 py-1.5 rounded-lg font-semibold bg-[#0F3F34] text-white hover:bg-[#0b3027] cursor-pointer"
                  >
                    {savingBudget ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
